import type {HVBroadcastActions} from 'src/action-creators/hv-broadcasts';

import type {
  KeyedHVBroadcastSuppression,
  fileCsvUploadEntries,
  ContactList,
  Filters,
  FileDetails,
  HVBroadcastSuppressionTimeFrame,
  HVBroadcastDataWithPagination,
  ScheduledOrNewHVBroadcast,
  PerformanceDetailsResponse,
} from 'src/types/hv-broadcasts';
import {
  RECEIVE_ALL_HV_BROADCASTS,
  RECEIVE_HV_BROADCAST_SUPPRESSION,
  RESTORE_HV_BROADCAST_PHONE_NUMBERS,
  SET_CSV_FILE_UPLOAD_HEADERS,
  SET_CSV_FILE_UPLOAD_ENTRIES,
  SET_CSV_FILE_UPLOAD,
  SET_PERFORMANCE_DATA,
  SET_MESSAGE_DATA,
  RECEIVE_HV_BROADCAST_FILTERS,
  RECEIVE_HV_CONTACT_LISTS,
  RECEIVE_AGENTS_RECENT_HV_CONTACT_LISTS,
  RECEIVE_HV_BROADCAST_SUPPRESSION_TIME_FRAME,
  RECEIVE_UPLOADED_FILE_DETAILS,
  REMOVE_UPLOADED_FILE_DETAILS,
  UPDATE_CONTACT_LIST_LIMIT_REACHED,
  UPDATE_CONTACT_LIST_ID_CREATED,
  CLEAR_UPLOADED_FILE_DETAILS,
  INITIALIZE_UPLOADED_FILE_DETAILS,
  RECEIVE_PERFORMANCE_DELIVERY_ANALYTICS,
  RECEIVE_HV_BROADCAST_UPDATE_NAME,
  RECEIVE_CURRENT_HV_BROADCAST_DATA,
  REMOVE_DELETED_HV_BROADCAST,
  SET_SCHEDULER_TEMPLATE_ID,
  SET_SHOW_RENAME_MODAL_POPUP_DETAILS,
  UPDATE_PERFORMANCE_DATA_NAME,
  CLEAR_CURRENT_BROADCAST_DATA,
  CLEAR_HV_BROADCASTS,
  SET_SELECTED_BROADCASTS_TAB,
  CLEAR_HV_BROADCAST_RESTORED_PHONE_NUMBERS,
  CLEAR_HV_BROADCAST_SUPPRESSION,
  SET_BROADCAST_DETAILS,
  RECEIVE_BROADCAST_PERFORMANCE_RECIPIENTS,
  SET_SELECTED_CHANNEL,
  SET_SUPPRESSION_API_COMPLETE,
  SET_CURRENTLY_APPLIED_HVB_SUPPRESSION,
} from 'src/action-creators/hv-broadcasts';

import keyBy from 'lodash/keyBy';


type HVBroadcastState = {
  hvBroadcasts: HVBroadcastDataWithPagination;
  hvBroadcastSuppression: KeyedHVBroadcastSuppression;
  hvBroadcastRestoredPhoneNumbers: string[];
  fileCsvUploadHeaders: string[];
  fileCsvUploadEntries: fileCsvUploadEntries[];
  fileCsvUpload: File | null;
  filters: Filters;
  contactLists: ContactList[];
  agentsRecentContactLists: ContactList[];
  broadcastSuppressionTimeFrame: HVBroadcastSuppressionTimeFrame;
  uploadedFileDetails: FileDetails[];
  contactListLimitReached: boolean;
  listCreatedId: string;
  hvBroadcastMessage: ScheduledOrNewHVBroadcast;
  hvBroadcastPerformance: PerformanceDetailsResponse;
  currentBroadcastData: ScheduledOrNewHVBroadcast;
  templateId: string;
  schedulerTemplateId: string;
  showRenameModalPopupDetails: string;
  selectedBroadcastType: string;
  broadcastDetails: ScheduledOrNewHVBroadcast;
  selectedChannel: string;
  placeholders: null | {
    [key: string]: string;
  };
  suppressionApiComplete: boolean;
};

const initialDraftMessage = {
  name: '',
  senderName: '',
  phoneNumberSetName: '',
  phoneNumberSetId: '',
  channel: 'sms',
  sentTime: '',
  messageBody: '',
  totalRecipients: 0,
  contactLists: [],
  files: [],
  grabbedNumbers: null,
  sendNow: true,
  id: '',
  schedulerTemplateId: '',
  placeholders: null,
  applySuppression: true,
} as const;

const initialHVBroadcasts = {
  broadcasts: {},
  page: 0,
  recordsPerPage: 0,
  totalPages: 0,
  totalRecords: 0,
} as const;

const initialTimeFrame = {
  broadcastSuppresionTimeType: 'DAY_3',
  time: {
    number: 3,
    unit: 'day',
  },
} as const;

const initialState: HVBroadcastState = {
  hvBroadcasts: initialHVBroadcasts,
  hvBroadcastSuppression: {},
  hvBroadcastRestoredPhoneNumbers: [],
  hvBroadcastPerformance: {
    deliveryAnalytics: {
      delivered: {
        total: 0,
        percent: 0,
      },
      responded: {
        total: 0,
        percent: 0,
      },
      failed: {
        total: 0,
        percent: 0,
      },
      optedOut: {
        total: 0,
        percent: 0,
      },
      skipped: {
        total: 0,
        percent: 0,
      },
      sent: {
        total: 0,
        percent: 0,
      },
    },
    name: '',
    page: 0,
    recipients: [],
    recordsPerPage: 0,
    sensePhone: '',
    totalPages: 0,
    totalRecipients: 0,
    totalRecords: 0,
    id: '',
  },
  // @ts-expect-error - TS2322 - Type '{ readonly name: ""; readonly senderName: ""; readonly phoneNumberSetName: ""; readonly phoneNumberSetId: ""; readonly channel: "sms"; readonly sentTime: ""; readonly messageBody: ""; ... 8 more ...; readonly applySuppression: true; }' is not assignable to type 'ScheduledOrNewHVBroadcast'.
  hvBroadcastMessage: initialDraftMessage,
  fileCsvUploadHeaders: [],
  fileCsvUploadEntries: [],
  fileCsvUpload: null,
  filters: {phoneNumberSets: [], sender: []},
  contactLists: [],
  agentsRecentContactLists: [],
  broadcastSuppressionTimeFrame: initialTimeFrame,
  uploadedFileDetails: [],
  contactListLimitReached: false,
  listCreatedId: '',
  // @ts-expect-error - TS2322 - Type '{ readonly name: ""; readonly senderName: ""; readonly phoneNumberSetName: ""; readonly phoneNumberSetId: ""; readonly channel: "sms"; readonly sentTime: ""; readonly messageBody: ""; ... 8 more ...; readonly applySuppression: true; }' is not assignable to type 'ScheduledOrNewHVBroadcast'.
  currentBroadcastData: initialDraftMessage,
  templateId: '',
  schedulerTemplateId: '',
  showRenameModalPopupDetails: '',
  selectedBroadcastType: 'sent',
  // @ts-expect-error - TS2322 - Type '{ readonly name: ""; readonly senderName: ""; readonly phoneNumberSetName: ""; readonly phoneNumberSetId: ""; readonly channel: "sms"; readonly sentTime: ""; readonly messageBody: ""; ... 8 more ...; readonly applySuppression: true; }' is not assignable to type 'ScheduledOrNewHVBroadcast'.
  broadcastDetails: initialDraftMessage,
  selectedChannel: 'sms',
  placeholders: null,
  suppressionApiComplete: false,
};

export default (
  state: HVBroadcastState | null | undefined = initialState,
  action: HVBroadcastActions,
): HVBroadcastState => {
  switch (action.type) {
    case RECEIVE_ALL_HV_BROADCASTS:
      const update = action.payload.update;
      const keyedBroadcasts = keyBy(action.payload.data.broadcasts, 'id');
      const broadcastDetails = {
        // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
        ...state.hvBroadcasts.broadcasts,
        ...keyedBroadcasts,
      } as const;
      // @ts-expect-error - TS2322 - Type '{ hvBroadcasts: { broadcasts: { readonly [x: string]: HVBData; }; page: number; recordsPerPage: number; totalPages: number; totalRecords: number; }; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 21 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        hvBroadcasts: update
          ? {
              ...action.payload.data,
              broadcasts: broadcastDetails,
            }
          : {...action.payload.data, broadcasts: keyedBroadcasts},
      };
    case RECEIVE_HV_BROADCAST_UPDATE_NAME:
      const {id, name} = action.payload;
      // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
      const currentBroadcast = state.hvBroadcasts.broadcasts[id];
      currentBroadcast.name = name;
      const allBroadcasts = {
        // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
        ...state.hvBroadcasts.broadcasts,
        [id]: currentBroadcast,
      } as const;
      // @ts-expect-error - TS2322 - Type '{ hvBroadcasts: { broadcasts: { readonly [x: string]: HVBData; }; page: number; recordsPerPage: number; totalPages: number; totalRecords: number; }; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 21 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
        hvBroadcasts: {...state.hvBroadcasts, broadcasts: allBroadcasts},
      };
    case RECEIVE_CURRENT_HV_BROADCAST_DATA:
      const {templateId = '', schedulerTemplateId = ''} = action.payload;
      // @ts-expect-error - TS2322 - Type '{ currentBroadcastData: ScheduledOrNewHVBroadcast; broadcastDetails: ScheduledOrNewHVBroadcast; templateId: string; schedulerTemplateId: string; ... 19 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        currentBroadcastData: action.payload,
        broadcastDetails: action.payload,
        templateId,
        schedulerTemplateId,
      };
    // @ts-expect-error - TS2678 - Type '"hv-broadcasts/removeDeletedHVBrpadcast"' is not comparable to type '"hv-broadcasts/receiveAllHVBroadcasts" | "hv-broadcasts/receiveHVBroadcastSuppression" | "hv-broadcasts/restoreHVBroadcastPhoneNumbers" | "hv-broadcasts/receiveHVBroadcastFilters" | ... 19 more ... | "hv-broadcasts/setCurrentlyAppliedHVBSuppression"'.
    case REMOVE_DELETED_HV_BROADCAST:
      const filteredBroadcasts =
        // @ts-expect-error - TS18047 - 'state' is possibly 'null'. | TS2339 - Property 'payload' does not exist on type 'never'.
        delete state.hvBroadcasts.broadcasts[action.payload];
      // @ts-expect-error - TS2322 - Type '{ hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 21 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {...state};
    case RECEIVE_HV_BROADCAST_SUPPRESSION:
      const filteredSuppression = action.payload.filter(
        (suppression) =>
          suppression.type === 'invalid' || suppression.type === 'landline',
      );
      // @ts-expect-error - TS2322 - Type '{ hvBroadcastSuppression: Dictionary<HVBroadcastSuppression>; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; ... 21 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        hvBroadcastSuppression: keyBy(filteredSuppression, 'phoneNumber'),
      };
    case RECEIVE_HV_BROADCAST_SUPPRESSION_TIME_FRAME:
      // @ts-expect-error - TS2322 - Type '{ broadcastSuppressionTimeFrame: HVBroadcastSuppressionTimeFrame; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; ... 21 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        broadcastSuppressionTimeFrame: action.payload,
      };
    case RESTORE_HV_BROADCAST_PHONE_NUMBERS:
      // @ts-expect-error - TS2322 - Type '{ hvBroadcastRestoredPhoneNumbers: string[]; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        hvBroadcastRestoredPhoneNumbers: [
          // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
          ...state.hvBroadcastRestoredPhoneNumbers,
          ...action.payload,
        ],
      };
    // @ts-expect-error - TS2678 - Type '"hv-broadcasts/clearHVBroadcastRestoredPhoneNumbers"' is not comparable to type '"hv-broadcasts/receiveAllHVBroadcasts" | "hv-broadcasts/receiveHVBroadcastSuppression" | "hv-broadcasts/restoreHVBroadcastPhoneNumbers" | "hv-broadcasts/receiveHVBroadcastFilters" | ... 19 more ... | "hv-broadcasts/setCurrentlyAppliedHVBSuppression"'.
    case CLEAR_HV_BROADCAST_RESTORED_PHONE_NUMBERS:
      // @ts-expect-error - TS2322 - Type '{ hvBroadcastRestoredPhoneNumbers: never[]; hvBroadcastSuppression: {}; broadcastSuppressionTimeFrame: { readonly broadcastSuppresionTimeType: "DAY_3"; readonly time: { readonly number: 3; readonly unit: "day"; }; }; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        hvBroadcastRestoredPhoneNumbers: [],
        hvBroadcastSuppression: {},
        broadcastSuppressionTimeFrame: initialTimeFrame,
      };
    // @ts-expect-error - TS2678 - Type '"hv-broadcasts/clearHVBroadcastSuppression"' is not comparable to type '"hv-broadcasts/receiveAllHVBroadcasts" | "hv-broadcasts/receiveHVBroadcastSuppression" | "hv-broadcasts/restoreHVBroadcastPhoneNumbers" | "hv-broadcasts/receiveHVBroadcastFilters" | ... 19 more ... | "hv-broadcasts/setCurrentlyAppliedHVBSuppression"'.
    case CLEAR_HV_BROADCAST_SUPPRESSION: {
      // @ts-expect-error - TS2322 - Type '{ hvBroadcastSuppression: {}; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastRestoredPhoneNumbers?: string[] | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        hvBroadcastSuppression: {},
      };
    }
    case SET_CSV_FILE_UPLOAD_HEADERS:
      // @ts-expect-error - TS2322 - Type '{ fileCsvUploadHeaders: string[]; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        fileCsvUploadHeaders: action.payload,
      };
    case SET_CSV_FILE_UPLOAD_ENTRIES:
      // @ts-expect-error - TS2322 - Type '{ fileCsvUploadEntries: fileCsvUploadEntries[]; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        fileCsvUploadEntries: action.payload,
      };
    case SET_CSV_FILE_UPLOAD:
      // @ts-expect-error - TS2322 - Type '{ fileCsvUpload: File | null; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        fileCsvUpload: action.payload,
      };
    case RECEIVE_HV_BROADCAST_FILTERS:
      // @ts-expect-error - TS2322 - Type '{ filters: Filters; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        filters: action.payload,
      };
    case SET_PERFORMANCE_DATA:
      // @ts-expect-error - TS2322 - Type '{ hvBroadcastPerformance: PerformanceDetailsResponse; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; ... 21 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {...state, hvBroadcastPerformance: action.payload};
    // @ts-expect-error - TS2678 - Type '"hv-broadcasts/updatePerformanceDataName"' is not comparable to type '"hv-broadcasts/receiveAllHVBroadcasts" | "hv-broadcasts/receiveHVBroadcastSuppression" | "hv-broadcasts/restoreHVBroadcastPhoneNumbers" | "hv-broadcasts/receiveHVBroadcastFilters" | ... 19 more ... | "hv-broadcasts/setCurrentlyAppliedHVBSuppression"'.
    case UPDATE_PERFORMANCE_DATA_NAME:
      // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'. | TS18047 - 'state' is possibly 'null'.
      if (action.payload.id === state.hvBroadcastPerformance.id) {
        // @ts-expect-error - TS2322 - Type '{ hvBroadcastPerformance: { name: any; deliveryAnalytics: PerformanceDeliveryAnalytics; totalRecipients: number; sensePhone: string; ... 6 more ...; countOnly?: boolean; }; ... 22 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
        return {
          ...state,
          hvBroadcastPerformance: {
            // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
            ...state.hvBroadcastPerformance,
            // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
            name: action.payload.name,
          },
        };
      } else {
        // @ts-expect-error - TS2322 - Type 'HVBroadcastState | null' is not assignable to type 'HVBroadcastState'.
        return state;
      }
    case RECEIVE_PERFORMANCE_DELIVERY_ANALYTICS:
      // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
      if (state.hvBroadcastPerformance.id === action.payload.id) {
        // @ts-expect-error - TS2322 - Type '{ hvBroadcastPerformance: { deliveryAnalytics: PerformanceDeliveryAnalytics; totalRecipients: number; sensePhone: string; name: string; recipients: BroadcastRecipient[]; ... 5 more ...; countOnly?: boolean; }; ... 22 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
        return {
          ...state,
          hvBroadcastPerformance: {
            // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
            ...state.hvBroadcastPerformance,
            deliveryAnalytics: action.payload.deliveryAnalytics,
          },
        };
      } else {
        // @ts-expect-error - TS2322 - Type 'HVBroadcastState | null' is not assignable to type 'HVBroadcastState'.
        return state;
      }
    case RECEIVE_BROADCAST_PERFORMANCE_RECIPIENTS:
      // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
      if (state.hvBroadcastPerformance.id === action.payload.id) {
        // @ts-expect-error - TS2322 - Type '{ hvBroadcastPerformance: { id: string; page: number; recipients: BroadcastRecipient[]; recordsPerPage: number; totalPages: number; totalRecipients: number; ... 4 more ...; name: string; }; ... 22 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
        return {
          ...state,
          hvBroadcastPerformance: {
            // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
            ...state.hvBroadcastPerformance,
            ...action.payload,
          },
        };
      } else {
        // @ts-expect-error - TS2322 - Type 'HVBroadcastState | null' is not assignable to type 'HVBroadcastState'.
        return state;
      }
    case SET_MESSAGE_DATA:
      // @ts-expect-error - TS2322 - Type '{ hvBroadcastMessage: ScheduledOrNewHVBroadcast; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {...state, hvBroadcastMessage: action.payload};
    case RECEIVE_HV_CONTACT_LISTS:
      const updateList = action.payload.update;
      const receivedContactList = action.payload.contactLists;
      // @ts-expect-error - TS2322 - Type '{ contactLists: ContactList[]; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        contactLists: updateList
          ? // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
            [...state.contactLists, ...receivedContactList]
          : receivedContactList,
      };
    // @ts-expect-error - TS2678 - Type '"hv-broadcasts/receiveAgentsRecentHVContactLists"' is not comparable to type '"hv-broadcasts/receiveAllHVBroadcasts" | "hv-broadcasts/receiveHVBroadcastSuppression" | "hv-broadcasts/restoreHVBroadcastPhoneNumbers" | "hv-broadcasts/receiveHVBroadcastFilters" | ... 19 more ... | "hv-broadcasts/setCurrentlyAppliedHVBSuppression"'.
    case RECEIVE_AGENTS_RECENT_HV_CONTACT_LISTS:
      // @ts-expect-error - TS2322 - Type '{ agentsRecentContactLists: any; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        agentsRecentContactLists: action.payload,
      };
    case RECEIVE_UPLOADED_FILE_DETAILS:
      // @ts-expect-error - TS2322 - Type '{ uploadedFileDetails: FileDetails[]; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
        uploadedFileDetails: [...state.uploadedFileDetails, action.payload],
      };
    case REMOVE_UPLOADED_FILE_DETAILS:
      // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
      const filteredResults = state.uploadedFileDetails.filter(
        (file) => file.id !== action.payload,
      );
      // @ts-expect-error - TS2322 - Type '{ uploadedFileDetails: FileDetails[]; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        uploadedFileDetails: filteredResults,
      };
    case UPDATE_CONTACT_LIST_LIMIT_REACHED:
      // @ts-expect-error - TS2322 - Type '{ contactListLimitReached: boolean; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        contactListLimitReached: action.payload,
      };
    case UPDATE_CONTACT_LIST_ID_CREATED:
      // @ts-expect-error - TS2322 - Type '{ listCreatedId: string; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        listCreatedId: action.payload,
      };
    case CLEAR_UPLOADED_FILE_DETAILS:
      // @ts-expect-error - TS2322 - Type '{ uploadedFileDetails: never[]; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        uploadedFileDetails: [],
      };
    case INITIALIZE_UPLOADED_FILE_DETAILS:
      // @ts-expect-error - TS2322 - Type '{ uploadedFileDetails: FileDetails[]; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        uploadedFileDetails: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"hv-broadcasts/setSchedulerTemplateId"' is not comparable to type '"hv-broadcasts/receiveAllHVBroadcasts" | "hv-broadcasts/receiveHVBroadcastSuppression" | "hv-broadcasts/restoreHVBroadcastPhoneNumbers" | "hv-broadcasts/receiveHVBroadcastFilters" | ... 19 more ... | "hv-broadcasts/setCurrentlyAppliedHVBSuppression"'.
    case SET_SCHEDULER_TEMPLATE_ID:
      // @ts-expect-error - TS2322 - Type '{ schedulerTemplateId: any; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'. | TS2339 - Property 'payload' does not exist on type 'never'.
      return {...state, schedulerTemplateId: action.payload};
    // @ts-expect-error - TS2678 - Type '"hv-broadcasts/showRenameModalPopupDetails"' is not comparable to type '"hv-broadcasts/receiveAllHVBroadcasts" | "hv-broadcasts/receiveHVBroadcastSuppression" | "hv-broadcasts/restoreHVBroadcastPhoneNumbers" | "hv-broadcasts/receiveHVBroadcastFilters" | ... 19 more ... | "hv-broadcasts/setCurrentlyAppliedHVBSuppression"'.
    case SET_SHOW_RENAME_MODAL_POPUP_DETAILS:
      // @ts-expect-error - TS2322 - Type '{ showRenameModalPopupDetails: any; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'. | TS2339 - Property 'payload' does not exist on type 'never'.
      return {...state, showRenameModalPopupDetails: action.payload};
    // @ts-expect-error - TS2678 - Type '"hv-broadcasts/clearCurrentBroadcastData"' is not comparable to type '"hv-broadcasts/receiveAllHVBroadcasts" | "hv-broadcasts/receiveHVBroadcastSuppression" | "hv-broadcasts/restoreHVBroadcastPhoneNumbers" | "hv-broadcasts/receiveHVBroadcastFilters" | ... 19 more ... | "hv-broadcasts/setCurrentlyAppliedHVBSuppression"'.
    case CLEAR_CURRENT_BROADCAST_DATA:
      // @ts-expect-error - TS2322 - Type '{ readonly name: ""; readonly senderName: ""; readonly phoneNumberSetName: ""; readonly phoneNumberSetId: ""; readonly channel: "sms"; readonly sentTime: ""; readonly messageBody: ""; ... 8 more ...; readonly applySuppression: true; }' is not assignable to type 'ScheduledOrNewHVBroadcast'.
      return {...state, currentBroadcastData: initialDraftMessage};
    // @ts-expect-error - TS2678 - Type '"hv-broadcasts/clearHVBroadcasts"' is not comparable to type '"hv-broadcasts/receiveAllHVBroadcasts" | "hv-broadcasts/receiveHVBroadcastSuppression" | "hv-broadcasts/restoreHVBroadcastPhoneNumbers" | "hv-broadcasts/receiveHVBroadcastFilters" | ... 19 more ... | "hv-broadcasts/setCurrentlyAppliedHVBSuppression"'.
    case CLEAR_HV_BROADCASTS:
      // @ts-expect-error - TS2322 - Type '{ hvBroadcasts: { readonly broadcasts: {}; readonly page: 0; readonly recordsPerPage: 0; readonly totalPages: 0; readonly totalRecords: 0; }; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 21 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {...state, hvBroadcasts: initialHVBroadcasts};
    // @ts-expect-error - TS2678 - Type '"hv-broadcasts/setSelectedBroadcastsTab"' is not comparable to type '"hv-broadcasts/receiveAllHVBroadcasts" | "hv-broadcasts/receiveHVBroadcastSuppression" | "hv-broadcasts/restoreHVBroadcastPhoneNumbers" | "hv-broadcasts/receiveHVBroadcastFilters" | ... 19 more ... | "hv-broadcasts/setCurrentlyAppliedHVBSuppression"'.
    case SET_SELECTED_BROADCASTS_TAB:
      // @ts-expect-error - TS2322 - Type '{ selectedBroadcastType: any; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'. | TS2339 - Property 'payload' does not exist on type 'never'.
      return {...state, selectedBroadcastType: action.payload};
    // @ts-expect-error - TS2678 - Type '"hv-broadcasts/setBroadcastDetails"' is not comparable to type '"hv-broadcasts/receiveAllHVBroadcasts" | "hv-broadcasts/receiveHVBroadcastSuppression" | "hv-broadcasts/restoreHVBroadcastPhoneNumbers" | "hv-broadcasts/receiveHVBroadcastFilters" | ... 19 more ... | "hv-broadcasts/setCurrentlyAppliedHVBSuppression"'.
    case SET_BROADCAST_DETAILS:
      // @ts-expect-error - TS2322 - Type '{ broadcastDetails: any; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'. | TS2339 - Property 'payload' does not exist on type 'never'.
      return {...state, broadcastDetails: action.payload};
    case SET_SELECTED_CHANNEL:
      // @ts-expect-error - TS2322 - Type '{ selectedChannel: string; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {...state, selectedChannel: action.payload};
    case SET_SUPPRESSION_API_COMPLETE:
      // @ts-expect-error - TS2322 - Type '{ suppressionApiComplete: boolean; hvBroadcasts?: HVBroadcastDataWithPagination | undefined; hvBroadcastSuppression?: KeyedHVBroadcastSuppression | undefined; ... 20 more ...; placeholders?: { ...; } | ... 1 more ... | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        suppressionApiComplete: action.payload,
      };
    case SET_CURRENTLY_APPLIED_HVB_SUPPRESSION:
      // @ts-expect-error - TS2322 - Type '{ broadcastDetails: { applySuppression: boolean; name: string; senderName: string; phoneNumberSetName: string; phoneNumberSetId: string; channel: string; sentTime: string; messageBody: string; ... 12 more ...; shouldShortenLinks?: boolean; }; ... 22 more ...; suppressionApiComplete?: boolean | undefined; }' is not assignable to type 'HVBroadcastState'.
      return {
        ...state,
        broadcastDetails: {
          // @ts-expect-error - TS18047 - 'state' is possibly 'null'.
          ...state.broadcastDetails,
          applySuppression: action.payload.value,
        },
      };
    default:
      // @ts-expect-error - TS2322 - Type 'HVBroadcastState | null' is not assignable to type 'HVBroadcastState'.
      return state;
  }
};
